import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MARCA, portalBase } from "@/lib/portal";
import Inmuebles from "@/components/dashboard/Inmuebles";
import PortalClientes from "./PortalClientes";
import PortalAdmin from "./PortalAdmin";
import PortalTitulares from "./PortalTitulares";
import PortalBarrido from "./PortalBarrido";
import PortalEscribano from "./PortalEscribano";
import PortalVigilancia from "./PortalVigilancia";
import PortalSalud from "./PortalSalud";
import InicioSecciones from "./InicioSecciones";
import { SeccionHero } from "./SeccionHero";
import PortalNovedades from "./PortalNovedades";
import PortalEstadisticas from "./PortalEstadisticas";
import { PortalLogin, PortalRegistro, PortalRecuperar } from "./PortalAuth";
import { ICONO, Wordmark, useFavicon } from "./brand";
import "./tierrascore.css";

function Guard({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!user || !(user.portal || user.portal_admin)) return <Navigate to={`${portalBase()}/login`} replace />;
  return children;
}

function Shell() {
  useFavicon();
  const { user, logout } = useAuth();
  const soloAdmin = !user.portal && user.portal_admin;   // el aprobador entra con su cuenta existente: solo Administración (sin datos de ninguna empresa)
  const [tab, setTab] = useState("inicio");
  const [k, setK] = useState(0);
  const tabs = [...(soloAdmin ? [] : [["inmuebles", "Inmuebles"], ["clientes", "Mis clientes"]]), ...(user.portal_admin ? [["titulares", "Buscar titular"]] : []), ...(soloAdmin ? [] : [["vigilancia", "Mi cartera"]]),
    ...(user.portal_admin ? [["novedades", "Cambios"], ["estadisticas", "Estadísticas"], ["escribano", "Informe del registro"], ["barrido", "Barrido"], ["salud", "Salud"], ["admin", "Admin"]] : [])];
  const [qTop, setQTop] = useState("");
  const [qBus, setQBus] = useState({ q: "", n: 0 });
  const buscar = (e) => { e.preventDefault(); if (!qTop.trim()) return; setQBus({ q: qTop.trim(), n: qBus.n + 1 }); setTab("titulares"); };
  const ini = (user.name || user.email || "?").split(/\s+/).map((x) => x[0]).slice(0, 2).join("").toUpperCase();
  return <div className="ts-app min-h-screen" data-testid="portal-app">
    <header className="ts-top cn-top px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
      <span className="flex items-center gap-2" data-testid="portal-marca"><img src={ICONO} alt="" className="w-8 h-8" /><Wordmark className="text-xl" /></span>
      <nav className="order-3 lg:order-none w-full lg:w-auto flex gap-1 overflow-x-auto">{[["inicio", "Inicio"], ...tabs].map(([id, l]) => <button key={id} onClick={() => setTab(id)} className={`ts-tab ${tab === id ? "on" : ""}`} data-testid={`portal-tab-${id}`}>{l}</button>)}</nav>
      <div className="ml-auto flex items-center gap-3">
        {user.portal_admin && <form onSubmit={buscar} className="hidden md:block"><input value={qTop} onChange={(e) => setQTop(e.target.value)} placeholder="Buscar titular o CI…" className="w-56 rounded-full border border-[#dfe4e1] bg-[#f6f8f7] px-4 py-1.5 text-sm outline-none focus:border-[#1f7a4d]" data-testid="portal-buscador" /></form>}
        <span className="hidden sm:block text-right text-xs leading-tight" data-testid="portal-usuario"><b className="block text-[#13211b]">{user.name}</b><span className="text-[#5f6f66]">{soloAdmin ? "Administración del portal" : (user.empresa_nombre || user.empresa)}</span></span>
        <span className="ts-avatar" aria-hidden>{ini}</span>
        <button onClick={logout} className="text-xs font-semibold text-[#5f6f66] hover:text-[#13211b]" data-testid="portal-logout">Salir</button>
      </div>
    </header>
    <main className="p-4 max-w-[1600px] mx-auto">
      {tab === "inicio" && <InicioSecciones tabs={tabs.length > 8 ? tabs.filter(([id]) => !["salud", "estadisticas"].includes(id)) : tabs} nombre={user.name} onIr={setTab} />}
      {tab !== "inicio" && <SeccionHero tab={tab} />}
      {tab === "inmuebles" && !soloAdmin && <Inmuebles key={k} isAdmin portal puedeBorrar={!!user.portal_admin} />}
      {tab === "clientes" && !soloAdmin && <PortalClientes onCambio={() => setK((x) => x + 1)} />}
      {tab === "admin" && user.portal_admin && <PortalAdmin />}
      {tab === "titulares" && user.portal_admin && <PortalTitulares qInicial={qBus} />}
      {tab === "barrido" && user.portal_admin && <PortalBarrido />}
      {tab === "escribano" && user.portal_admin && <PortalEscribano />}
      {tab === "novedades" && user.portal_admin && <PortalNovedades />}
      {tab === "estadisticas" && user.portal_admin && <PortalEstadisticas />}
      {tab === "salud" && user.portal_admin && <PortalSalud />}
      {tab === "vigilancia" && !soloAdmin && <PortalVigilancia />}
    </main>
    <footer className="text-center text-[11px] text-slate-500 py-4">{MARCA.nombre} · fuentes públicas: SNC (catastro), Sentinel‑2 / ESA WorldCover, Open‑Meteo, Esri World Imagery</footer>
  </div>;
}

export default function PortalApp() {
  useEffect(() => {
    document.documentElement.classList.remove("tm-oscuro");   // el portal es siempre claro y con marca propia
    document.title = MARCA.nombre;
    const m = document.querySelector('meta[name="description"]'); if (m) m.setAttribute("content", MARCA.lema);
    const ico = document.querySelector('link[rel="icon"]'); if (ico) ico.setAttribute("href", "/portal-favicon.svg");
  }, []);
  return <Routes>
    <Route path="login" element={<PortalLogin />} />
    <Route path="registro" element={<PortalRegistro />} />
    <Route path="recuperar" element={<PortalRecuperar />} />
    <Route path="*" element={<Guard><Shell /></Guard>} />
  </Routes>;
}
